import React, { useState, useEffect } from "react";
import {
    Typography,
    TextField,
    Button,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Tooltip,
    MenuItem,
    FormControl,
    Chip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Grid,
    Paper,
    Avatar,
    Fade,
    Container,
    Divider,
    useTheme,
    alpha,
    InputLabel,
    Select,
    Switch,
    SelectChangeEvent,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Web as WebIcon,
    Business as BusinessIcon,
    Event as EventIcon,
    Settings as SettingsIcon,
    Link as LinkIcon,
    CheckCircle,
    Cancel,
    Info,
    Email as EmailIcon,
    Verified,
    NotificationImportant,
    Chat,
    Rocket,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';

interface TildaFormData {
    id: number;
    form_id: string;
    form_name: string;
    description: string;
    pipeline_id: string;
    create_lead: boolean;
    create_event: boolean;
    create_mm_informer: boolean;
    create_deploy: boolean;
    event_id: string;
    mm_channel_id: string;
    require_email_verification: boolean;
    verification_template_id: number;
    processing_template_id: number;
    deploy_template_id?: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    verification_template?: {
        id: number;
        name: string;
        subject: string;
    };
    processing_template?: {
        id: number;
        name: string;
        subject: string;
    };
    deploy_template?: {
        id: number;
        name: string;
    };
}

const TildaForms: React.FC = observer(() => {
    const { tildaStore, emailTemplateStore, deploymentStore } = useStore();
    const theme = useTheme();
    const [newForm, setNewForm] = useState({
        form_id: "",
        form_name: "",
        description: "",
        pipeline_id: "",
        create_lead: false,
        create_event: false,
        create_mm_informer: false,
        create_deploy: false,
        event_id: "",
        mm_channel_id: "",
        require_email_verification: false,
        verification_template_id: 0,
        processing_template_id: 0,
        deploy_template_id: 0,
    });
    const [editingForm, setEditingForm] = useState<TildaFormData | null>(null);
    const [openModal, setOpenModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            await Promise.all([
                tildaStore.fetchForms(),
                tildaStore.fetchTags(),
                tildaStore.fetchEvents(),
                tildaStore.fetchTildaPages(),
                tildaStore.fetchMMChannels(),
                emailTemplateStore.fetchTemplates(),
                deploymentStore.fetchTemplates(),
            ]);
        };
        loadData();
    }, [tildaStore, emailTemplateStore, deploymentStore]);

    const handleNewFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
        const { name, value } = event.target;
        const target = event.target as HTMLInputElement;

        if (target.type === "checkbox") {
            setNewForm((prev) => ({
                ...prev,
                [name]: target.checked
            }));
        } else if (name === "form_id") {
            const selectedForm = tildaStore.tildaForms.find((form) => form.id === value);
            if (selectedForm) {
                setNewForm((prev) => ({
                    ...prev,
                    form_id: selectedForm.id,
                    form_name: selectedForm.name
                }));
            }
        } else {
            setNewForm((prev) => ({
                ...prev,
                [name]: name.includes('template_id') ? Number(value) : value
            }));
        }
    };

    const handleEditFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
        const { name, value } = event.target;
        const target = event.target as HTMLInputElement;

        if (target.type === "checkbox") {
            setEditingForm((prev) => prev ? {
                ...prev,
                [name]: target.checked
            } : null);
        } else {
            setEditingForm((prev) => prev ? {
                ...prev,
                [name]: value
            } : null);
        }
    };

    const addNewForm = async () => {
        if (newForm.form_id && newForm.description) {
            const success = await tildaStore.createForm({
                form_id: newForm.form_id,
                form_name: newForm.form_name,
                description: newForm.description,
                pipeline_id: newForm.pipeline_id,
                create_lead: newForm.create_lead,
                create_event: newForm.create_event,
                create_mm_informer: newForm.create_mm_informer,
                create_deploy: newForm.create_deploy,
                event_id: newForm.event_id,
                mm_channel_id: newForm.mm_channel_id,
                require_email_verification: newForm.require_email_verification,
                verification_template_id: newForm.verification_template_id,
                processing_template_id: newForm.processing_template_id,
                deploy_template_id: newForm.deploy_template_id,
            });

            if (success) {
                setNewForm({
                    form_id: "",
                    form_name: "",
                    description: "",
                    pipeline_id: "",
                    create_lead: false,
                    create_event: false,
                    create_mm_informer: false,
                    create_deploy: false,
                    event_id: "",
                    mm_channel_id: "",
                    require_email_verification: true,
                    verification_template_id: 0,
                    processing_template_id: 0,
                    deploy_template_id: 0,
                });
                setOpenModal(false);
            }
        }
    };

    const handleEditForm = (form: TildaFormData) => {
        setEditingForm({ ...form });
        setOpenEditModal(true);
    };

    const updateForm = async () => {
        if (editingForm) {
            const success = await tildaStore.updateForm(editingForm.id, {
                form_name: editingForm.form_name,
                description: editingForm.description,
                pipeline_id: editingForm.pipeline_id,
                create_lead: editingForm.create_lead,
                create_event: editingForm.create_event,
                create_mm_informer: editingForm.create_mm_informer,
                create_deploy: editingForm.create_deploy,
                event_id: editingForm.event_id,
                mm_channel_id: editingForm.mm_channel_id,
                require_email_verification: editingForm.require_email_verification,
                verification_template_id: editingForm.verification_template_id,
                processing_template_id: editingForm.processing_template_id,
                deploy_template_id: editingForm.deploy_template_id,
                is_active: editingForm.is_active,
            });

            if (success) {
                setOpenEditModal(false);
                setEditingForm(null);
            }
        }
    };

    const handleRemoveForm = async (id: number) => {
        if (window.confirm('Вы уверены, что хотите удалить это сопоставление?')) {
            await tildaStore.deleteForm(id);
        }
    };

    const MetricCard = ({ title, value, icon, color = 'primary' }: {
        title: string;
        value: number;
        icon: React.ReactNode;
        color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
    }) => (
        <Paper
            sx={{
                p: 3,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                },
            }}
        >
            <Avatar
                sx={{
                    bgcolor: alpha(theme.palette[color].main, 0.1),
                    color: theme.palette[color].main,
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 2,
                }}
            >
                {icon}
            </Avatar>
            <Typography variant="h4" fontWeight={700} color={`${color}.main`}>
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {title}
            </Typography>
        </Paper>
    );

    const FormCard = ({ form }: { form: TildaFormData }) => (
        <Fade in timeout={300}>
            <Card
                sx={{
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    opacity: form.is_active ? 1 : 0.7,
                    border: '1px solid',
                    borderColor: form.is_active ? 'divider' : alpha(theme.palette.error.main, 0.3),
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                    }
                }}
            >
                {!form.is_active && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            zIndex: 1,
                        }}
                    >
                        <Avatar sx={{ bgcolor: 'error.main', width: 32, height: 32 }}>
                            <Cancel fontSize="small" />
                        </Avatar>
                    </Box>
                )}
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box display="flex" alignItems="flex-start" gap={2}>
                            <Avatar
                                sx={{
                                    bgcolor: form.is_active ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                    color: form.is_active ? 'primary.main' : 'grey.500',
                                    width: 48,
                                    height: 48,
                                }}
                            >
                                <WebIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    {form.description}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {form.form_name}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    ID формы:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {form.form_id}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Email верификация */}
                        <Grid item xs={12}>
                            <Chip
                                icon={form.require_email_verification ? <Verified /> : <NotificationImportant />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{form.require_email_verification ? 'Требует верификацию email' : 'Без верификации email'}</span>
                                        {form.require_email_verification && form.verification_template && (
                                            <Box component="span" sx={{ fontSize: '0.8em', opacity: 0.8 }}>
                                                ({form.verification_template.name})
                                            </Box>
                                        )}
                                    </Box>
                                }
                                color={form.require_email_verification ? "info" : "default"}
                                variant="outlined"
                                sx={{ width: '100%', justifyContent: 'flex-start' }}
                            />
                        </Grid>

                        {/* Шаблон обработки */}
                        {form.processing_template && (
                            <Grid item xs={12}>
                                <Chip
                                    icon={<EmailIcon />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>Уведомление об обработке</span>
                                            <Box component="span" sx={{ fontSize: '0.8em', opacity: 0.8 }}>
                                                ({form.processing_template.name})
                                            </Box>
                                        </Box>
                                    }
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                                />
                            </Grid>
                        )}

                        {form.create_lead && (
                            <Grid item xs={12}>
                                <Chip
                                    icon={<BusinessIcon />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>Создание лида</span>
                                            {form.pipeline_id && (
                                                <strong>
                                                    {tildaStore.tags.find(t => t.id === form.pipeline_id)?.name || form.pipeline_id}
                                                </strong>
                                            )}
                                        </Box>
                                    }
                                    color="success"
                                    variant="outlined"
                                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                                />
                            </Grid>
                        )}

                        {form.create_event && (
                            <Grid item xs={12}>
                                <Chip
                                    icon={<EventIcon />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>Привязка к событию</span>
                                            {form.event_id && (
                                                <strong>
                                                    {tildaStore.events.find(e => e.id === form.event_id)?.name || form.event_id}
                                                </strong>
                                            )}
                                        </Box>
                                    }
                                    color="info"
                                    variant="outlined"
                                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                                />
                            </Grid>
                        )}

                        {form.create_mm_informer && (
                            <Grid item xs={12}>
                                <Chip
                                    icon={<Chat />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>Информирование в Mattermost</span>
                                            {form.mm_channel_id && (
                                                <strong>
                                                    #{tildaStore.mm_channels.find(e => e.id === form.mm_channel_id)?.name || form.mm_channel_id}
                                                </strong>
                                            )}
                                        </Box>
                                    }
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                                />
                            </Grid>
                        )}

                        {form.create_deploy && (
                            <Grid item xs={12}>
                                <Chip
                                    icon={<Rocket />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>Требуется развертывание</span>
                                            {form.deploy_template && (
                                                <strong>
                                                    {form.deploy_template.name}
                                                </strong>
                                            )}
                                        </Box>
                                    }
                                    color="warning"
                                    variant="outlined"
                                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                                />
                            </Grid>
                        )}

                        {!form.create_lead && !form.create_event && !form.create_mm_informer && !form.create_deploy && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    <Typography variant="body2">
                                        Только сбор данных
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Chip
                        label={form.is_active ? 'Активна' : 'Неактивна'}
                        color={form.is_active ? 'success' : 'error'}
                        size="small"
                        icon={form.is_active ? <CheckCircle /> : <Cancel />}
                    />
                    <Box>
                        <Tooltip title="Редактировать">
                            <IconButton
                                onClick={() => handleEditForm(form)}
                                color="primary"
                                size="small"
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                            <IconButton
                                onClick={() => handleRemoveForm(form.id)}
                                color="error"
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </CardActions>
            </Card>
        </Fade>
    );

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1
                            }}
                        >
                            Формы Tilda
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Настройка интеграции форм с CRM системой
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenModal(true)}
                        size="large"
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.35)',
                            },
                        }}
                    >
                        Добавить форму
                    </Button>
                </Box>

                {tildaStore.error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {tildaStore.error}
                    </Alert>
                )}

                {/* Статистика */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Всего форм"
                            value={tildaStore.forms.length}
                            icon={<WebIcon />}
                            color="primary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Активных форм"
                            value={tildaStore.activeForms.length}
                            icon={<CheckCircle />}
                            color="success"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Создают лиды"
                            value={tildaStore.forms.filter(f => f.create_lead).length}
                            icon={<BusinessIcon />}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Привязаны к событиям"
                            value={tildaStore.forms.filter(f => f.create_event).length}
                            icon={<EventIcon />}
                            color="warning"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Информируют в Mattermost"
                            value={tildaStore.forms.filter(f => f.create_mm_informer).length}
                            icon={<Chat />}
                            color="secondary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Требуют развертывание"
                            value={tildaStore.forms.filter(f => f.create_deploy).length}
                            icon={<Rocket />}
                            color="warning"
                        />
                    </Grid>
                </Grid>

                {tildaStore.loading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10}>
                        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
                        <Typography variant="h6" color="textSecondary">
                            Загрузка форм...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {tildaStore.forms.length > 0 ? (
                            <Grid container spacing={3}>
                                {tildaStore.forms.map((form) => (
                                    <Grid item xs={12} sm={6} md={4} key={form.id}>
                                        <FormCard form={form} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Paper
                                sx={{
                                    p: 8,
                                    textAlign: 'center',
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.5)} 0%, ${alpha(theme.palette.grey[50], 0.5)} 100%)`,
                                }}
                            >
                                <WebIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3, opacity: 0.5 }} />
                                <Typography variant="h5" color="textSecondary" gutterBottom fontWeight={600}>
                                    Нет настроенных форм
                                </Typography>
                                <Typography variant="body1" color="textSecondary" paragraph sx={{ maxWidth: 400, mx: 'auto' }}>
                                    Создайте первую интеграцию с Tilda формой для автоматической передачи заявок в CRM
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenModal(true)}
                                    size="large"
                                >
                                    Добавить форму
                                </Button>
                            </Paper>
                        )}
                    </>
                )}
            </Box>

            {/* Modal for Adding New Form */}
            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <AddIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>
                                Добавить новую форму
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setOpenModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Описание формы"
                                name="description"
                                value={newForm.description}
                                onChange={handleNewFormChange}
                                placeholder="Например: Форма обратной связи на главной странице"
                                required
                                helperText="Краткое описание для удобной идентификации"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Форма Tilda</InputLabel>
                                <Select
                                    label="Форма Tilda"
                                    name="form_id"
                                    value={newForm.form_id}
                                    onChange={handleNewFormChange}
                                >
                                    {tildaStore.tildaForms.map((tildaForm) => (
                                        <MenuItem key={tildaForm.id} value={tildaForm.id}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <WebIcon fontSize="small" color="action" />
                                                {tildaForm.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="require_email_verification"
                                            checked={newForm.require_email_verification}
                                            onChange={handleNewFormChange}
                                            color="info"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Verified color="info" />
                                            <span>Требовать подтверждение email</span>
                                        </Box>
                                    }
                                />
                            </Paper>
                        </Grid>

                        {newForm.require_email_verification && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Шаблон письма верификации</InputLabel>
                                    <Select
                                        label="Шаблон письма верификации"
                                        name="verification_template_id"
                                        value={String(newForm.verification_template_id || 0)}
                                        onChange={handleNewFormChange}
                                    >
                                        <MenuItem value={0}>По умолчанию</MenuItem>
                                        {emailTemplateStore.activeTemplates.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                {template.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Шаблон письма обработки</InputLabel>
                                <Select
                                    label="Шаблон письма обработки"
                                    name="processing_template_id"
                                    value={String(newForm.processing_template_id || 0)}
                                    onChange={handleNewFormChange}
                                >
                                    <MenuItem value={0}>По умолчанию</MenuItem>
                                    {emailTemplateStore.activeTemplates.map((template) => (
                                        <MenuItem key={template.id} value={template.id}>
                                            {template.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="create_lead"
                                            checked={newForm.create_lead}
                                            onChange={handleNewFormChange}
                                            color="success"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <BusinessIcon color="success" />
                                            <span>Создавать лид в CRM</span>
                                        </Box>
                                    }
                                />
                            </Paper>
                        </Grid>

                        {newForm.create_lead && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Тег/Группа в CRM</InputLabel>
                                    <Select
                                        label="Тег/Группа в CRM"
                                        name="pipeline_id"
                                        value={newForm.pipeline_id}
                                        onChange={handleNewFormChange}
                                    >
                                        {tildaStore.tags.map((tag) => (
                                            <MenuItem key={tag.id} value={tag.id}>
                                                <Chip
                                                    label={tag.name}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="create_event"
                                            checked={newForm.create_event}
                                            onChange={handleNewFormChange}
                                            color="warning"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <EventIcon color="warning" />
                                            <span>Прикреплять контакт к мероприятию</span>
                                        </Box>
                                    }
                                />
                            </Paper>
                        </Grid>

                        {newForm.create_event && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Выберите мероприятие</InputLabel>
                                    <Select
                                        label="Выберите мероприятие"
                                        name="event_id"
                                        value={newForm.event_id}
                                        onChange={handleNewFormChange}
                                    >
                                        {tildaStore.events.map((event) => (
                                            <MenuItem key={event.id} value={event.id}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <EventIcon fontSize="small" color="action" />
                                                    {event.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="create_mm_informer"
                                            checked={newForm.create_mm_informer}
                                            onChange={handleNewFormChange}
                                            color="secondary"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Chat color="secondary" />
                                            <span>Информировать в Mattermost</span>
                                        </Box>
                                    }
                                />
                            </Paper>
                        </Grid>

                        {newForm.create_mm_informer && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Выберите канал Mattermost</InputLabel>
                                    <Select
                                        label="Выберите канал Mattermost"
                                        name="mm_channel_id"
                                        value={newForm.mm_channel_id}
                                        onChange={handleNewFormChange}
                                    >
                                        {tildaStore.mm_channels.map((mm_channel) => (
                                            <MenuItem key={mm_channel.id} value={mm_channel.id}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Chat fontSize="small" color="action" />
                                                    #{mm_channel.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="create_deploy"
                                            checked={newForm.create_deploy}
                                            onChange={handleNewFormChange}
                                            color="warning"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Rocket color="warning" />
                                            <span>Требуется развертывание</span>
                                        </Box>
                                    }
                                />
                            </Paper>
                        </Grid>

                        {newForm.create_deploy && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Шаблон развертывания</InputLabel>
                                    <Select
                                        label="Шаблон развертывания"
                                        name="deploy_template_id"
                                        value={String(newForm.deploy_template_id || 0)}
                                        onChange={handleNewFormChange}
                                    >
                                        {deploymentStore.templates.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Rocket fontSize="small" color="action" />
                                                    {template.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setOpenModal(false)}
                        disabled={tildaStore.saving}
                        variant="outlined"
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        onClick={addNewForm}
                        disabled={tildaStore.saving || !newForm.form_id || !newForm.description}
                        startIcon={tildaStore.saving ? <CircularProgress size={20} /> : <AddIcon />}
                        sx={{
                            minWidth: 140,
                            background: tildaStore.saving ? 'grey.300' : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        }}
                    >
                        {tildaStore.saving ? 'Добавление...' : 'Добавить'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal for Editing Form */}
            <Dialog
                open={openEditModal}
                onClose={() => setOpenEditModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <EditIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>
                                Редактировать форму
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setOpenEditModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {editingForm && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Описание"
                                    name="description"
                                    value={editingForm.description}
                                    onChange={handleEditFormChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Название формы"
                                    name="form_name"
                                    value={editingForm.form_name}
                                    onChange={handleEditFormChange}
                                    disabled
                                    helperText="Название формы нельзя изменить"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: editingForm.is_active ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1) }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="is_active"
                                                checked={editingForm.is_active}
                                                onChange={handleEditFormChange}
                                                color={editingForm.is_active ? "success" : "error"}
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {editingForm.is_active ? <CheckCircle color="success" /> : <Cancel color="error" />}
                                                <span>{editingForm.is_active ? 'Форма активна' : 'Форма неактивна'}</span>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="require_email_verification"
                                                checked={editingForm.require_email_verification}
                                                onChange={handleEditFormChange}
                                                color="info"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Verified color="info" />
                                                <span>Требовать подтверждение email</span>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            {editingForm.require_email_verification && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Шаблон письма верификации</InputLabel>
                                        <Select
                                            label="Шаблон письма верификации"
                                            name="verification_template_id"
                                            value={String(editingForm.verification_template_id || 0)}
                                            onChange={handleEditFormChange}
                                        >
                                            <MenuItem value={0}>По умолчанию</MenuItem>
                                            {emailTemplateStore.activeTemplates.map((template) => (
                                                <MenuItem key={template.id} value={template.id}>
                                                    {template.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Шаблон письма обработки</InputLabel>
                                    <Select
                                        label="Шаблон письма обработки"
                                        name="processing_template_id"
                                        value={String(editingForm.processing_template_id || 0)}
                                        onChange={handleEditFormChange}
                                    >
                                        <MenuItem value={0}>По умолчанию</MenuItem>
                                        {emailTemplateStore.activeTemplates.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                {template.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="create_lead"
                                                checked={editingForm.create_lead}
                                                onChange={handleEditFormChange}
                                                color="success"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <BusinessIcon color="success" />
                                                <span>Создавать лид в CRM</span>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            {editingForm.create_lead && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Тег/Группа в CRM</InputLabel>
                                        <Select
                                            label="Тег/Группа в CRM"
                                            name="pipeline_id"
                                            value={editingForm.pipeline_id || ""}
                                            onChange={handleEditFormChange}
                                        >
                                            {tildaStore.tags.map((tag) => (
                                                <MenuItem key={tag.id} value={tag.id}>
                                                    <Chip
                                                        label={tag.name}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="create_event"
                                                checked={editingForm.create_event}
                                                onChange={handleEditFormChange}
                                                color="warning"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <EventIcon color="warning" />
                                                <span>Прикреплять контакт к мероприятию</span>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            {editingForm.create_event && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Выберите мероприятие</InputLabel>
                                        <Select
                                            label="Выберите мероприятие"
                                            name="event_id"
                                            value={editingForm.event_id || ""}
                                            onChange={handleEditFormChange}
                                        >
                                            {tildaStore.events.map((event) => (
                                                <MenuItem key={event.id} value={event.id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <EventIcon fontSize="small" color="action" />
                                                        {event.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="create_mm_informer"
                                                checked={editingForm.create_mm_informer}
                                                onChange={handleEditFormChange}
                                                color="secondary"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chat color="secondary" />
                                                <span>Информировать в Mattermost</span>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            {editingForm.create_mm_informer && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Выберите канал Mattermost</InputLabel>
                                        <Select
                                            label="Выберите канал Mattermost"
                                            name="mm_channel_id"
                                            value={editingForm.mm_channel_id || ""}
                                            onChange={handleEditFormChange}
                                        >
                                            {tildaStore.mm_channels.map((mm_channel) => (
                                                <MenuItem key={mm_channel.id} value={mm_channel.id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Chat fontSize="small" color="action" />
                                                        #{mm_channel.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="create_deploy"
                                                checked={editingForm.create_deploy}
                                                onChange={handleEditFormChange}
                                                color="warning"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Rocket color="warning" />
                                                <span>Требуется развертывание</span>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            {editingForm.create_deploy && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Шаблон развертывания</InputLabel>
                                        <Select
                                            label="Шаблон развертывания"
                                            name="deploy_template_id"
                                            value={String(editingForm.deploy_template_id || 0)}
                                            onChange={handleEditFormChange}
                                        >
                                            {deploymentStore.templates.map((template) => (
                                                <MenuItem key={template.id} value={template.id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Rocket fontSize="small" color="action" />
                                                        {template.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setOpenEditModal(false)}
                        disabled={tildaStore.saving}
                        variant="outlined"
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        onClick={updateForm}
                        disabled={tildaStore.saving}
                        startIcon={tildaStore.saving ? <CircularProgress size={20} /> : <SettingsIcon />}
                        sx={{
                            minWidth: 140,
                            background: tildaStore.saving ? 'grey.300' : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        }}
                    >
                        {tildaStore.saving ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Информационная панель */}
            <Paper
                sx={{
                    p: 3,
                    mt: 4,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Info color="info" />
                    <Typography variant="h6" fontWeight={700}>
                        Как работает интеграция
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" paragraph>
                            <strong>1. Настройка в Tilda:</strong> Создайте форму в Tilda и настройте отправку данных на webhook URL вашего сервиса.
                        </Typography>
                        <Typography variant="body2" paragraph>
                            <strong>2. Создание лида:</strong> При включении этой опции каждая заявка из формы будет автоматически создавать новый лид в CRM с указанным тегом.
                        </Typography>
                        <Typography variant="body2" paragraph>
                            <strong>3. Прикрепление к мероприятию:</strong> Контакт будет автоматически добавлен к выбранному мероприятию в системе.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" paragraph>
                            <strong>4. Развертывание:</strong> Если включено, для каждой заявки будет создано развертывание по выбранному шаблону.
                        </Typography>
                        <Typography variant="body2" paragraph>
                            <strong>5. Уведомления:</strong> Можно настроить отправку писем для верификации email и обработки заявки.
                        </Typography>
                        <Typography variant="body2">
                            <strong>6. Активность:</strong> Неактивные формы не будут обрабатываться системой, но сохранят свои настройки для будущего использования.
                        </Typography>
                    </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        <strong>Webhook URL для Tilda:</strong> {`${window.location.origin}/api/tilda/webhook`}
                    </Typography>
                </Alert>
            </Paper>
        </Container>
    );
});

export default TildaForms;